/* ProjectsCrudCard.tsx
    Une card affichant la gestion CRUD des projets
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import {
	PROJECT_STATUS_CONFIG,
	ProjectData,
	projectsApi,
} from 'features/projects';
import { faBriefcase } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface ProjectsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

const PROJECT_STATUS_LABELS: Record<string, string> = Object.fromEntries(
	PROJECT_STATUS_CONFIG.steps.map((step) => [step.key, step.label])
);

// ProjectsCrudCard component
export const ProjectsCrudCard: React.FC<ProjectsCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/projects',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: ProjectData) => a.id }]
			: []),
		// Colonne "Référence"
		{
			label: 'Référence',
			field: 'referenceNumber',
			render: (a: ProjectData) => a.referenceNumber,
		},
		// Colonne "Libellé" = objet de la dernière version du devis lié
		{
			label: 'Libellé',
			field: 'none',
			render: (a: ProjectData) =>
				a.latestActiveQuoteVersion?.commentIndex || '—',
			exportValue: (a: ProjectData) =>
				a.latestActiveQuoteVersion?.commentIndex ?? '',
		},
		// Colonne "Devis d'origine" — référence du devis lié (export uniquement, pas de filtre/tri pertinent)
		// NB : déplacé en exportOnlyColumns ci-dessous
		// Colonne "Entreprise" — tiers issu du devis, filtre/tri via le JOIN backend sur 'tiers.label'
		{
			label: 'Entreprise',
			field: 'tiers.label',
			render: (a: ProjectData) => a.quote?.tiers?.label ?? '—',
			exportValue: (a: ProjectData) => a.quote?.tiers?.label ?? '',
			linkUrl: (a: ProjectData) =>
				a.quote?.tiers ? `/app/tiers/${a.quote.tiers.id}/edit` : '',
		},
		// Colonne "Chargé d'affaire" — issu du businessManager de la dernière version active du devis
		// filtre/tri via le JOIN backend sur 'businessManager.lastName' (toutes versions confondues)
		{
			label: "Chargé d'affaire",
			field: 'businessManager.lastName',
			render: (a: ProjectData) => {
				const versions = a.quote?.quoteVersions ?? [];
				const activeVersions = versions.filter((v) => v.status !== 'CANCELED');
				const latest =
					activeVersions.length > 0
						? activeVersions.reduce((prev, cur) =>
								(cur.versionIndex ?? 0) > (prev.versionIndex ?? 0) ? cur : prev
							)
						: versions.length > 0
							? versions.reduce((prev, cur) =>
									(cur.versionIndex ?? 0) > (prev.versionIndex ?? 0)
										? cur
										: prev
								)
							: null;
				const bm = latest?.businessManager;
				if (!bm) return '—';
				return `${bm.lastName ?? ''} ${bm.firstName ?? ''}`.trim() || '—';
			},
			exportValue: (a: ProjectData) => {
				const versions = a.quote?.quoteVersions ?? [];
				const activeVersions = versions.filter((v) => v.status !== 'CANCELED');
				const latest =
					activeVersions.length > 0
						? activeVersions.reduce((prev, cur) =>
								(cur.versionIndex ?? 0) > (prev.versionIndex ?? 0) ? cur : prev
							)
						: versions.length > 0
							? versions.reduce((prev, cur) =>
									(cur.versionIndex ?? 0) > (prev.versionIndex ?? 0)
										? cur
										: prev
								)
							: null;
				const bm = latest?.businessManager;
				if (!bm) return '';
				return `${bm.firstName ?? ''} ${bm.lastName ?? ''}`.trim();
			},
		},
		// Colonne "Statut"
		{
			label: 'Statut',
			field: 'status',
			render: (a: ProjectData) =>
				PROJECT_STATUS_LABELS[a.status ?? ''] ?? a.status ?? '—',
			exportValue: (a: ProjectData) =>
				PROJECT_STATUS_LABELS[a.status ?? ''] ?? a.status ?? '',
		},
		// Colonne "Total facturé HT" — somme des totalExclTax des factures client liées
		// Disponible uniquement en mode extended (trop chargé en sm)
		...(extended
			? [
					{
						label: 'Total facturé HT',
						field: 'none',
						render: (a: ProjectData) => {
							const total = (a.customerInvoices ?? []).reduce(
								(sum, inv) => sum + Number(inv.totalExclTax ?? 0),
								0
							);
							return new Intl.NumberFormat('fr-FR', {
								style: 'currency',
								currency: 'EUR',
							}).format(total);
						},
						exportValue: (a: ProjectData) =>
							(a.customerInvoices ?? []).reduce(
								(sum, inv) => sum + Number(inv.totalExclTax ?? 0),
								0
							),
					},
				]
			: []),
	];

	return (
		<GenericCrudCard<ProjectData>
			queryKey="projects"
			title={size === 'sm' ? 'Affaires' : 'Liste des affaires'}
			fetchData={(token, options) => projectsApi.listPaginated(token, options)}
			deleteItem={projectsApi.delete}
			tableColumns={columns}
			exportOnlyColumns={[
				// Devis d'origine — export uniquement (pas de filtre/tri pertinent car relation imbriquée)
				{
					label: 'Devis',
					exportValue: (a: ProjectData) => a.quote?.referenceNumber ?? '',
				},
				{
					label: 'Heures activités non facturées',
					exportValue: (a: ProjectData) =>
						(a.activities ?? [])
							.filter((act) => !act.customerInvoice)
							.reduce((sum, act) => sum + Number(act.durationHours ?? 0), 0),
				},
				{
					label: 'Heures activités facturées',
					exportValue: (a: ProjectData) =>
						(a.activities ?? [])
							.filter((act) => !!act.customerInvoice)
							.reduce((sum, act) => sum + Number(act.durationHours ?? 0), 0),
				},
				{
					label: 'Heures activités total',
					exportValue: (a: ProjectData) =>
						(a.activities ?? []).reduce(
							(sum, act) => sum + Number(act.durationHours ?? 0),
							0
						),
				},
				// --- Activités : montants HT ---
				{
					label: 'Montant HT activités non facturées',
					exportValue: (a: ProjectData) =>
						(a.activities ?? [])
							.filter((act) => !act.customerInvoice)
							.reduce((sum, act) => sum + Number(act.unitPriceExclTax ?? 0), 0),
				},
				{
					label: 'Montant HT activités facturées',
					exportValue: (a: ProjectData) =>
						(a.activities ?? [])
							.filter((act) => !!act.customerInvoice)
							.reduce((sum, act) => sum + Number(act.unitPriceExclTax ?? 0), 0),
				},
				{
					label: 'Montant HT activités total',
					exportValue: (a: ProjectData) =>
						(a.activities ?? []).reduce(
							(sum, act) => sum + Number(act.unitPriceExclTax ?? 0),
							0
						),
				},
				// --- Éléments logistiques : montants HT (PU × quantité) ---
				{
					label: 'Montant HT logistique non facturé',
					exportValue: (a: ProjectData) =>
						(a.logisticElements ?? [])
							.filter((el) => !el.customerInvoice)
							.reduce(
								(sum, el) =>
									sum +
									Number(el.unitPriceExclTax ?? 0) * Number(el.quantity ?? 1),
								0
							),
				},
				{
					label: 'Montant HT logistique facturé',
					exportValue: (a: ProjectData) =>
						(a.logisticElements ?? [])
							.filter((el) => !!el.customerInvoice)
							.reduce(
								(sum, el) =>
									sum +
									Number(el.unitPriceExclTax ?? 0) * Number(el.quantity ?? 1),
								0
							),
				},
				{
					label: 'Montant HT logistique total',
					exportValue: (a: ProjectData) =>
						(a.logisticElements ?? []).reduce(
							(sum, el) =>
								sum +
								Number(el.unitPriceExclTax ?? 0) * Number(el.quantity ?? 1),
							0
						),
				},
			]}
			addButtonLabel="Ajouter une affaire"
			getAddPath={() => '/app/projects/new'}
			getEditPath={(id) => `/app/projects/${id}/edit`}
			emptyMessage="Aucune affaire trouvée."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faBriefcase : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};
export default ProjectsCrudCard;
